import React, { useState, useContext, useEffect } from "react";
import { css } from "emotion";

import { colors, textSizes, boxStyles } from "../styles/theme";
import TitleBlock from "../components/TitleBlock";
import GenericButton from "../components/GenericButton";
import SwitchHeader from "../components/SwitchHeader";
import auth from "../services/api/auth";
import { withRouter, useParams, useLocation } from "react-router-dom";
import { GlobalContext } from "../context/GlobalState";
import { useForm } from "react-hook-form";
import ErrorMessage from "../components/ErrorMessage";

const SignUp = ({ history }) => {
  useEffect(() => {
    document.title = "Signup - Spotlight";
  });

  const query = new URLSearchParams(useLocation().search);
  const { initUser, currentWorkspace } = useContext(GlobalContext);
  const methods = useForm();
  const { register, handleSubmit, setError, errors } = methods;
  const [isLoading, setLoading] = useState(false);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const coupon = query.get("coupon");
  const planId = query.get("planId");

  if (coupon && currentWorkspace && !hasCheckedAuth) {
    setHasCheckedAuth(true);
    history.push(`/plans?coupon=${coupon}`);
  } else if (currentWorkspace && planId && !hasCheckedAuth) {
    setHasCheckedAuth(true);
    history.push(`/checkout/${planId}`);
  } else if (currentWorkspace && !hasCheckedAuth) {
    setHasCheckedAuth(true);
    history.push('/home');
  }

  if (coupon) {
    sessionStorage.setItem("coupon", coupon);
  }

  const doSignUp = async ({ firstName, lastName, email, password }) => {
    const signUpData = {
      firstName,
      lastName,
      emailAddress: email,
      password,
    };

    try {
      const { data } = await auth.signUp(signUpData);
      // console.log(data);
      const userData = {
        id: data.id,
        emailAddress: data.emailAddress,
        firstName: data.firstName,
        lastName: data.lastName,
        invites: data.invites,
      };
      initUser(userData);

      localStorage.setItem('embedPulse', 'true');

      if (data.invites.length > 0) {
        history.push("/invitations");
      } else if (query.get("planId")) {
        history.push(`/signup/workspace?planId=${query.get("planId")}`);
      } else {
        history.push("/signup/workspace");
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 409) {
          setError(
            "signup",
            "emailAlreadyInUse",
            "This email is already in use. If you own the account, try logging in."
          );
        } else {
          setError(
            "signup",
            "badCombo",
            "There was a problem creating your account. Please reload and try again."
          );
        }
      } else if (error.request) {
        setError(
          "signup",
          "noConnection",
          "You seem to have gotten disconnected. Make sure your internet is connected and try again."
        );
        console.log(error.request);
      } else {
        setError(
          "signup",
          "unexpected",
          "There was a problem creating your account. Please reload and try again."
        );
        console.log("Error", error.message);
      }

      setLoading(false);
    }
  };

  return (
    <div className={container}>
      <SwitchHeader />
      <div className="body">
        <div className="centerBlock">
          <div className="hero">
            <TitleBlock
              title="Hello Beautiful,"
              subtitle="Enter some info below to create an account"
            />
          </div>
          <form className="form">
            {errors.signup && (
              <ErrorMessage>{errors.signup.message}</ErrorMessage>
            )}
            <div className="formItem">
              <div className="names">
                <div className="nameFirst">
                  <label>
                    <p>FIRST NAME</p>
                    <input
                      name="firstName"
                      type="text"
                      ref={register({
                        required: {
                          value: true,
                          message: "First name is required",
                        },
                      })}
                    />
                    {errors.firstName && (
                      <ErrorMessage>{errors.firstName.message}</ErrorMessage>
                    )}
                  </label>
                </div>
                <div className="nameLast">
                  <label>
                    <p>LAST NAME</p>
                    <input
                      name="lastName"
                      type="text"
                      ref={register({
                        required: {
                          value: true,
                          message: "Last name is required",
                        },
                      })}
                    />
                  </label>
                  {errors.lastName && (
                    <ErrorMessage>{errors.lastName.message}</ErrorMessage>
                  )}
                </div>
              </div>
            </div>
            <div className="formItem">
              <label>
                <p>EMAIL</p>
                <input
                  name="email"
                  type="email"
                  ref={register({
                    required: {
                      value: true,
                      message: "Email is required",
                    },
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                      message: "Invalid email address",
                    },
                  })}
                />
              </label>
              {errors.email && (
                <ErrorMessage>{errors.email.message}</ErrorMessage>
              )}
            </div>
            <div className="formItem">
              <label>
                <p>PASSWORD</p>
                <input
                  name="password"
                  type="password"
                  ref={register({
                    required: {
                      value: true,
                      message: "Password is required",
                    },
                    minLength: {
                      value: 6,
                      message: "Must have at least 6 characters",
                    },
                  })}
                />
              </label>
              {errors.password && (
                <ErrorMessage>{errors.password.message}</ErrorMessage>
              )}
            </div>
            <div className="buttonSection">
              <div className="button">
                <GenericButton
                  text="Create Account"
                  disabled={isLoading}
                  handleClick={handleSubmit(doSignUp)}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default withRouter(SignUp);

const container = css`
  display: flex;
  flex-direction: column;

  .body {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .centerBlock {
    max-width: 650px;
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
  }

  .hero {
    max-width: 650px;
    width: 100%;
    padding: 40px 0;
  }
  .form {
    width: 80%;
    @media screen and (min-width: ${boxStyles.desktopBreak}) {
      width: 70%;
    }
    .buttonSection {
      display: flex;
      flex: 1;
      justify-content: center;
      padding: 10px;
      margin-top: 24px;

      .button {
        width: 50%;
      }
      button:focus {
        outline: none;
      }
    }
  }
  .formItem {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 10px;
    flex: 1;

    label {
      input {
        color: ${colors.inputTextColor};
      }
    }

    input {
      flex: 1;
      background-color: ${colors.inputBackground};
      padding: 10px;
      border: none;
      border-radius: ${boxStyles.borderRadius};
      color: ${colors.inputTextColor};
      font-size: ${textSizes.normal};
      width: 100%;
    }

    .names {
      display: flex;
      flex: 1;
      justify-content: space-between;

      .nameFirst {
        display: flex;
        flex: 1;
        justify-content: space-between;

        input {
          flex: 1;
          background-color: ${colors.inputBackground};
          padding: 10px;
          border: none;
          border-radius: ${boxStyles.borderRadius};
          color: ${colors.inputTextColor};
          font-size: ${textSizes.normal};
          width: 80%;
        }
      }
      .nameLast {
        display: flex;
        flex-direction: column;
        flex: 1;

        label {
          align-self: flex-end;
          width: 100%;
        }

        input {
          flex: 1;
          background-color: ${colors.inputBackground};
          padding: 10px;
          border: none;
          border-radius: ${boxStyles.borderRadius};
          font-size: ${textSizes.normal};
          width: 100%;
          box-sizing: border-box;
        }
      }
    }
  }

  label {
    display: flex;
    flex-direction: column;
    flex: 1;
    font-size: ${textSizes.normal};
    color: ${colors.placeholderText};
  }

  .passwordInput {
    p {
      bottom: 10px;
      right: 10px;
    }
  }

  input:focus {
    outline: none;
  }
`;
